import { apiBase, withApiKey } from './apiAuth';
import {
  getSessionTemplatePassword,
  setSessionTemplatePassword,
} from './templatePassword';

/**
 * OpenPolotno Templates ImagesGrid hardcodes getCredit → uniqueId (patched to
 * title) and does not set img title/aria-label. Watch the side panel for
 * preview cards: attach real titles, fix .credit spans, and add passworded
 * delete for user-* templates.
 */

const CACHE_TTL_MS = 30_000;
const STATUS_EVENT = 'design-studio:status';

type CatalogItem = {
  uniqueId?: string;
  title?: string;
  preview?: string;
};

type TitleMaps = {
  /** preview path / template asset path → title */
  byPath: Map<string, string>;
  /** uniqueId → title */
  byId: Map<string, string>;
};

let cachedMaps: TitleMaps | null = null;
let cachedAt = 0;
let fetchInFlight: Promise<TitleMaps> | null = null;

function emitStatus(message: string) {
  window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail: { message } }));
}

/** Normalize preview URL to a path key for matching (ignore origin/query). */
export function previewPathKey(url: string): string {
  try {
    const u = new URL(url, window.location.origin);
    return u.pathname.replace(/\/+$/, '') || '/';
  } catch {
    const bare = String(url || '').split('?')[0].split('#')[0];
    return bare.replace(/\/+$/, '') || bare;
  }
}

/** Extract template uniqueId from an img src like /api/templates/user-….png */
export function uniqueIdFromPreviewSrc(src: string): string | null {
  const key = previewPathKey(src);
  const m = key.match(/\/api\/templates\/([^/]+?)(?:\.(?:png|jpg|jpeg|webp|svg))?$/i);
  if (!m) return null;
  try {
    return decodeURIComponent(m[1]);
  } catch {
    return m[1];
  }
}

async function fetchTitleMaps(force = false): Promise<TitleMaps> {
  const now = Date.now();
  if (!force && cachedMaps && now - cachedAt < CACHE_TTL_MS) {
    return cachedMaps;
  }
  if (fetchInFlight) return fetchInFlight;

  fetchInFlight = (async () => {
    const byPath = new Map<string, string>();
    const byId = new Map<string, string>();
    try {
      const res = await fetch(withApiKey(`${apiBase()}/api/get-templates?page=1`));
      const data = (await res.json().catch(() => ({}))) as { items?: CatalogItem[] };
      for (const item of data.items || []) {
        const title = (item.title || '').trim();
        const id = (item.uniqueId || '').trim();
        if (id && title) byId.set(id, title);
        if (!title) continue;
        if (item.preview) {
          byPath.set(previewPathKey(item.preview), title);
        }
        if (id) {
          byPath.set(previewPathKey(`/api/templates/${id}.png`), title);
          byPath.set(previewPathKey(`/api/templates/${encodeURIComponent(id)}.png`), title);
        }
      }
    } catch (err) {
      console.warn('templateHoverTitles: failed to load catalog', err);
      if (cachedMaps) return cachedMaps;
    }
    cachedMaps = { byPath, byId };
    cachedAt = Date.now();
    return cachedMaps;
  })();

  try {
    return await fetchInFlight;
  } finally {
    fetchInFlight = null;
  }
}

function findTemplatesPanelRoot(): HTMLElement | null {
  return (
    document.querySelector<HTMLElement>('.raeditor-side-panel') ||
    document.querySelector<HTMLElement>('.bp5-navbar.raeditor-side-panel') ||
    document.body
  );
}

function resolveTitle(src: string, maps: TitleMaps): string | undefined {
  const key = previewPathKey(src);
  let title = maps.byPath.get(key);
  if (title) return title;

  const id = uniqueIdFromPreviewSrc(src);
  if (id) {
    title = maps.byId.get(id);
    if (title) return title;
    title =
      maps.byPath.get(previewPathKey(`/api/templates/${id}.png`)) ||
      maps.byPath.get(previewPathKey(`/api/templates/${encodeURIComponent(id)}.png`));
  }
  return title;
}

function findCardFrame(img: HTMLImageElement): HTMLElement | null {
  // ImageFrame is position:relative; ImageWrapper is the padded hover parent
  let el: HTMLElement | null = img.parentElement;
  while (el && el !== document.body) {
    const style = window.getComputedStyle(el);
    if (style.position === 'relative' || style.position === 'absolute') {
      return el;
    }
    // Prefer a short climb; don't leave the card
    if (el.querySelector?.('.credit') && el.contains(img)) return el;
    el = el.parentElement;
  }
  return img.parentElement;
}

function applyTitlesAndCredits(root: ParentNode, maps: TitleMaps): number {
  let applied = 0;
  const imgs = root.querySelectorAll<HTMLImageElement>('img[src]');
  imgs.forEach((img) => {
    const src = img.currentSrc || img.src || img.getAttribute('src') || '';
    if (!src) return;
    if (src.startsWith('data:') && src.length < 200) return;
    // Only care about template previews for credit/delete path
    const isTemplatePreview = /\/api\/templates\//i.test(previewPathKey(src));

    const title = resolveTitle(src, maps);
    if (title) {
      if (img.getAttribute('title') !== title || img.getAttribute('aria-label') !== title) {
        img.setAttribute('title', title);
        img.setAttribute('aria-label', title);
        applied += 1;
      }
    }

    if (!isTemplatePreview) return;

    const frame = findCardFrame(img);
    if (!frame) return;

    // Belt-and-suspenders: replace .credit span text with mapped title
    if (title) {
      const credit = frame.querySelector<HTMLElement>('.credit');
      if (credit) {
        const span = credit.querySelector('span') || credit;
        if (span.textContent !== title) {
          span.textContent = title;
          applied += 1;
        }
      }
    }

    ensureDeleteButton(frame, img, src, title, maps);
  });
  return applied;
}

function trashIconSvg(): string {
  return `<svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"><path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/><path d="M10 11v6M14 11v6" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>`;
}

function ensureDeleteButton(
  frame: HTMLElement,
  img: HTMLImageElement,
  src: string,
  title: string | undefined,
  maps: TitleMaps,
) {
  const id = uniqueIdFromPreviewSrc(src);
  if (!id || !id.startsWith('user-')) {
    frame.querySelector('.ds-tmpl-delete')?.remove();
    return;
  }

  // Make frame a positioning context for the absolute button
  if (window.getComputedStyle(frame).position === 'static') {
    frame.style.position = 'relative';
  }

  let btn = frame.querySelector<HTMLButtonElement>(':scope > .ds-tmpl-delete');
  if (!btn) {
    btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'ds-tmpl-delete';
    btn.innerHTML = trashIconSvg();
    btn.setAttribute('aria-label', 'Delete template');
    frame.appendChild(btn);
  }

  const displayTitle = title || maps.byId.get(id) || id;
  btn.title = `Delete “${displayTitle}”`;
  btn.setAttribute('data-tmpl-id', id);

  if (!(btn as HTMLButtonElement & { __dsBound?: boolean }).__dsBound) {
    (btn as HTMLButtonElement & { __dsBound?: boolean }).__dsBound = true;
    btn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      void handleDeleteClick(btn!, frame, img);
    });
  }
}

async function handleDeleteClick(
  btn: HTMLButtonElement,
  frame: HTMLElement,
  img: HTMLImageElement,
) {
  const id = btn.getAttribute('data-tmpl-id') || uniqueIdFromPreviewSrc(img.src || '') || '';
  if (!id.startsWith('user-')) return;

  const title =
    img.getAttribute('title') ||
    cachedMaps?.byId.get(id) ||
    id;

  if (!window.confirm(`Delete template "${title}"?`)) {
    return;
  }

  let password = getSessionTemplatePassword();
  if (!password) {
    const typed = window.prompt('Enter delete password:');
    if (typed === null) return;
    password = typed;
  }
  if (!password.trim()) {
    emitStatus('Delete cancelled — password required');
    return;
  }

  btn.disabled = true;
  try {
    const res = await fetch(withApiKey(`${apiBase()}/api/templates/${encodeURIComponent(id)}`), {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'X-Delete-Password': password,
      },
      body: JSON.stringify({ password }),
    });
    const data = (await res.json().catch(() => ({}))) as {
      ok?: boolean;
      error?: string;
      message?: string;
      deleted?: string[];
    };
    if (!res.ok) {
      const msg = data.message || data.error || `Delete failed (${res.status})`;
      emitStatus(`Delete failed: ${msg}`);
      btn.disabled = false;
      return;
    }

    setSessionTemplatePassword(password.trim());
    emitStatus(`Deleted ${id} (${data.deleted?.length ?? 0} keys)`);

    // Remove the card: climb to ImageWrapper (padded hover parent) when possible
    const card =
      frame.parentElement && frame.parentElement.querySelector('img') === img
        ? frame.parentElement
        : frame;
    card.remove();

    // Invalidate catalog cache so titles stay accurate
    cachedMaps = null;
    cachedAt = 0;
  } catch (err) {
    console.error(err);
    emitStatus('Delete failed — network error');
    btn.disabled = false;
  }
}


const TEMPLATE_SAVED_EVENT = 'design-studio:template-saved';

export type TemplateSavedDetail = {
  uniqueId: string;
  preview?: string;
  updatedAt?: string;
};

/** Append or replace ?v= cache-buster on a preview URL. */
export function withPreviewCacheBust(url: string, version?: string): string {
  const v = version || String(Date.now());
  try {
    const u = new URL(url, window.location.origin);
    u.searchParams.set('v', v);
    // Keep relative if input was relative
    if (url.startsWith('/') || url.startsWith('./')) {
      return `${u.pathname}?${u.searchParams.toString()}`;
    }
    return u.toString();
  } catch {
    const bare = String(url || '').split('?')[0];
    return `${bare}?v=${encodeURIComponent(v)}`;
  }
}

/**
 * Force-reload template panel <img> elements matching a uniqueId (or all user templates).
 * Also invalidates the title catalog cache and re-runs hover titles.
 */
export function bustTemplatePreviewImages(uniqueId?: string, version?: string): void {
  const v = version || String(Date.now());
  const root =
    document.querySelector('.raeditor-side-panel') ||
    document.querySelector('.bp5-navbar.raeditor-side-panel') ||
    document.body;
  if (!root) return;
  const imgs = root.querySelectorAll<HTMLImageElement>('img[src]');
  imgs.forEach((img) => {
    const src = img.getAttribute('src') || img.src || '';
    if (!/\/api\/templates\//i.test(src)) return;
    const id = uniqueIdFromPreviewSrc(src);
    if (uniqueId && id !== uniqueId) return;
    const next = withPreviewCacheBust(src, v);
    if (img.getAttribute('src') !== next) {
      img.setAttribute('src', next);
    }
  });
}

/**
 * Call after POST/PUT save-as-template succeeds so thumbnails refresh.
 */
export function notifyTemplateSaved(detail: TemplateSavedDetail): void {
  cachedMaps = null;
  cachedAt = 0;
  const v =
    detail.updatedAt ||
    (detail.preview && (() => {
      try {
        return new URL(detail.preview, window.location.origin).searchParams.get('v') || '';
      } catch {
        return '';
      }
    })()) ||
    String(Date.now());
  bustTemplatePreviewImages(detail.uniqueId, v);
  window.dispatchEvent(new CustomEvent(TEMPLATE_SAVED_EVENT, { detail }));
}

export type TemplateHoverTitlesHandle = {
  stop: () => void;
  refresh: () => Promise<void>;
};

/**
 * Start watching the DOM for template preview cards: titles, credit text, delete.
 * Call once from App (useEffect). Returns a stop handle.
 */
export function startTemplateHoverTitles(): TemplateHoverTitlesHandle {
  let stopped = false;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let pollTimer: ReturnType<typeof setInterval> | null = null;
  let observer: MutationObserver | null = null;

  const run = async (forceFetch = false) => {
    if (stopped) return;
    const maps = await fetchTitleMaps(forceFetch);
    if (stopped) return;
    const root = findTemplatesPanelRoot();
    if (root) applyTitlesAndCredits(root, maps);
  };

  const schedule = () => {
    if (stopped) return;
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      void run(false);
    }, 120);
  };

  const root = findTemplatesPanelRoot() || document.body;
  observer = new MutationObserver(() => schedule());
  observer.observe(root, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['src'],
  });

  pollTimer = setInterval(() => {
    void run(false);
  }, 4000);

  void run(true);

  const onSaved = (ev: Event) => {
    const detail = (ev as CustomEvent<TemplateSavedDetail>).detail;
    const v = detail?.updatedAt || String(Date.now());
    bustTemplatePreviewImages(detail?.uniqueId, v);
    void run(true);
  };
  window.addEventListener(TEMPLATE_SAVED_EVENT, onSaved);

  return {
    stop: () => {
      stopped = true;
      if (debounceTimer) clearTimeout(debounceTimer);
      if (pollTimer) clearInterval(pollTimer);
      observer?.disconnect();
      observer = null;
      window.removeEventListener(TEMPLATE_SAVED_EVENT, onSaved);
    },
    refresh: () => run(true),
  };
}

export { STATUS_EVENT as TEMPLATE_PANEL_STATUS_EVENT, TEMPLATE_SAVED_EVENT };
